import React, { useState, useCallback } from 'react'
import { useTranslation } from 'react-i18next'
import { mdiArrowLeft, mdiArrowRight, mdiCheckCircleOutline, mdiCheckboxMarkedOutline, mdiCloseCircleOutline, mdiEyeOffOutline, mdiPencil, mdiPlus } from '@mdi/js'
import { AppIcon } from '../ui/icon'
import { useDroppable } from '@dnd-kit/core'
import { Button } from '../ui/button'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../ui/alert-dialog'
import { cn } from '../../lib/utils'
import { worksiteCostDisplay, TRADE_META, COMPLIANCE_COLORS } from '../../lib/capex-utils'
import type { Worksite, TradeCategory, ComplianceLevel } from './types'
import { useCapexCurrency } from './CapexCurrencyContext'

interface WorksiteCellProps {
  worksite: Worksite
  horizonStart: number
  horizonEnd: number
  isReadOnly: boolean
  onUpdate: (uid: string, body: Record<string, unknown>, ifMatch: string) => void
  onDelete: (uid: string) => void
  onAddPackage?: (worksiteUid: string) => void
  onEditWorksite?: (worksite: Worksite) => void
  isDragging?: boolean
  complianceLevel?: ComplianceLevel | null
}

const STATUS_BADGE_CLASSES: Record<string, string> = {
  draft: 'bg-secondary text-secondary-foreground',
  reviewed: 'bg-blue-100 text-blue-800',
  validated: 'bg-emerald-100 text-emerald-800',
  deferred: 'bg-amber-100 text-amber-800',
  ignored: 'bg-muted text-muted-foreground',
}

export const WorksiteCell = React.memo(function WorksiteCell({
  worksite,
  horizonStart,
  horizonEnd,
  isReadOnly,
  onUpdate,
  onDelete,
  onAddPackage,
  onEditWorksite,
  isDragging,
  complianceLevel: level,
}: WorksiteCellProps) {
  const capexCurrency = useCapexCurrency()
  const { t } = useTranslation('capex')
  const [hovered, setHovered] = useState(false)
  const [ignoreDialogOpen, setIgnoreDialogOpen] = useState(false)

  const trade = worksite.trade as TradeCategory
  const tradeMeta = TRADE_META[trade]
  const defaultBorderColor = tradeMeta?.color ?? 'border-border'

  // Override border color for breach/overdue compliance
  const borderColorClass =
    level === 'breach' || level === 'overdue'
      ? COMPLIANCE_COLORS[level].border
      : defaultBorderColor

  const packages = worksite.work_packages ?? []
  const quotedCount = packages.filter((p) => p.quote_amount != null).length
  const totalPackages = packages.length

  const status = worksite.status ?? 'draft'
  const statusBadgeClass = STATUS_BADGE_CLASSES[status] ?? STATUS_BADGE_CLASSES.draft

  const year = worksite.year ?? 0
  const ifMatch = worksite.updated_at ?? ''

  const handleValidate = useCallback(() => {
    const newStatus = status === 'validated' ? 'reviewed' : 'validated'
    onUpdate(worksite.uid!, { status: newStatus }, ifMatch)
  }, [worksite.uid, status, ifMatch, onUpdate])

  const handleMarkReviewed = useCallback(() => {
    onUpdate(worksite.uid!, { status: 'reviewed' }, ifMatch)
  }, [worksite.uid, ifMatch, onUpdate])

  const handleDefer = useCallback(() => {
    if (year >= horizonEnd) return
    const targetYear = year + 1
    const originalYear = worksite.deferred_from_year ?? year
    const body: Record<string, unknown> = { year: targetYear }
    if (status !== 'validated') {
      if (targetYear > originalYear) {
        body.status = 'deferred'
        body.deferred_from_year = originalYear
      } else {
        body.status = 'draft'
        body.deferred_from_year = null
      }
    }
    onUpdate(worksite.uid!, body, ifMatch)
  }, [worksite.uid, worksite.deferred_from_year, year, horizonEnd, status, ifMatch, onUpdate])

  const handleAdvance = useCallback(() => {
    if (year <= horizonStart) return
    const targetYear = year - 1
    const originalYear = worksite.deferred_from_year ?? year
    const body: Record<string, unknown> = { year: targetYear }
    if (status !== 'validated') {
      if (targetYear > originalYear) {
        body.status = 'deferred'
        body.deferred_from_year = originalYear
      } else {
        // At or before the original year — clear deferred state
        body.status = 'draft'
        body.deferred_from_year = null
      }
    }
    onUpdate(worksite.uid!, body, ifMatch)
  }, [worksite.uid, worksite.deferred_from_year, year, horizonStart, status, ifMatch, onUpdate])

  const handleIgnoreConfirm = useCallback(() => {
    onDelete(worksite.uid!)
  }, [worksite.uid, onDelete])

  return (
    <>
      <div
        className={cn(
          'relative rounded-sm border-l-4 bg-card p-2 text-left min-w-28 transition-shadow',
          borderColorClass,
          hovered && !isReadOnly && 'shadow-md ring-1 ring-border',
          isDragging && 'opacity-50',
          !isReadOnly && 'cursor-grab active:cursor-grabbing'
        )}
        onMouseEnter={() => setHovered(true)}
        onMouseLeave={() => setHovered(false)}
        data-testid={`worksite-cell-${worksite.uid}`}
      >
        {/* Compliance badge */}
        {level && (
          <div className="mb-1">
            <span
              className={cn('text-xs px-1.5 py-0.5 rounded-full font-medium', COMPLIANCE_COLORS[level].badge)}
              aria-label={`Compliance: ${t(`compliance.${level}`)}`}
              data-testid={`worksite-compliance-badge-${worksite.uid}`}
            >
              {t(`compliance.${level}`)}
            </span>
          </div>
        )}

        {/* Cost display */}
        <div className="text-sm font-semibold text-foreground leading-tight">
          {worksiteCostDisplay(worksite, capexCurrency)}
        </div>

        {/* Quote progress */}
        {totalPackages > 0 && (
          <div className="text-xs text-muted-foreground mt-0.5">
            {quotedCount}/{totalPackages} quoted
          </div>
        )}

        {/* Status badge */}
        <div className="mt-1">
          <span
            className={cn('text-xs px-1.5 py-0.5 rounded-full font-medium', statusBadgeClass)}
            aria-label={`Status: ${t(`status.${status}`, status)}`}
            data-testid={`worksite-status-badge-${worksite.uid}`}
          >
            {t(`status.${status}`, status)}
          </span>
        </div>

        {/* Hover actions — only when not readOnly */}
        {hovered && !isReadOnly && (
          <div className="absolute top-0 right-0 mt-1 mr-1 flex flex-col gap-0.5 z-10 bg-card rounded p-0.5 shadow-sm">
            {/* Add work package */}
            <Button
              variant="ghost"
              size="sm"
              className="h-6 w-6 p-0"
              onClick={() => onAddPackage?.(worksite.uid!)}
              title={t('actions.add_package', 'Add work package')}
              data-testid={`worksite-action-add-package-${worksite.uid}`}
            >
              <AppIcon path={mdiPlus} size="xs" className="text-primary" />
            </Button>

            {/* Edit worksite */}
            <Button
              variant="ghost"
              size="sm"
              className="h-6 w-6 p-0"
              onClick={() => onEditWorksite?.(worksite)}
              title={t('actions.edit_worksite', 'Edit worksite')}
              data-testid={`worksite-action-edit-${worksite.uid}`}
            >
              <AppIcon path={mdiPencil} size="xs" />
            </Button>

            {/* Validate / Unvalidate */}
            <Button
              variant="ghost"
              size="sm"
              className="h-6 w-6 p-0"
              onClick={handleValidate}
              title={status === 'validated' ? t('actions.unvalidate', 'Unvalidate') : t('actions.validate', 'Validate')}
              data-testid={`worksite-action-validate-${worksite.uid}`}
            >
              {status === 'validated' ? (
                <AppIcon path={mdiCloseCircleOutline} size="xs" className="text-destructive" />
              ) : (
                <AppIcon path={mdiCheckCircleOutline} size="xs" className="text-emerald-600" />
              )}
            </Button>

            {/* Mark as reviewed (only for draft) */}
            {status === 'draft' && (
              <Button
                variant="ghost"
                size="sm"
                className="h-6 w-6 p-0"
                onClick={handleMarkReviewed}
                title={t('actions.mark_reviewed', 'Mark as reviewed')}
                data-testid={`worksite-action-review-${worksite.uid}`}
              >
                <AppIcon path={mdiCheckboxMarkedOutline} size="xs" className="text-blue-600" />
              </Button>
            )}

            {/* Defer +1 year */}
            <Button
              variant="ghost"
              size="sm"
              className="h-6 w-6 p-0"
              onClick={handleDefer}
              disabled={year >= horizonEnd}
              title={t('actions.defer', 'Defer +1 year')}
              data-testid={`worksite-action-defer-${worksite.uid}`}
            >
              <AppIcon path={mdiArrowRight} size="xs" className="text-amber-600" />
            </Button>

            {/* Advance -1 year */}
            <Button
              variant="ghost"
              size="sm"
              className="h-6 w-6 p-0"
              onClick={handleAdvance}
              disabled={year <= horizonStart}
              title={t('actions.advance', 'Advance -1 year')}
              data-testid={`worksite-action-advance-${worksite.uid}`}
            >
              <AppIcon path={mdiArrowLeft} size="xs" className="text-amber-600" />
            </Button>

            {/* Ignore */}
            <Button
              variant="ghost"
              size="sm"
              className="h-6 w-6 p-0"
              onClick={() => setIgnoreDialogOpen(true)}
              title={t('actions.ignore', 'Ignore')}
              data-testid={`worksite-action-ignore-${worksite.uid}`}
            >
              <AppIcon path={mdiEyeOffOutline} size="xs" />
            </Button>
          </div>
        )}
      </div>

      {/* Ignore confirmation dialog */}
      <AlertDialog open={ignoreDialogOpen} onOpenChange={setIgnoreDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t('actions.ignore', 'Ignore worksite')}</AlertDialogTitle>
            <AlertDialogDescription>
              {t('ignore_confirm_description', 'This worksite will be marked as ignored and hidden from the plan. You can undo this by changing its status.')}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>{t('create_plan_dialog.cancel', 'Cancel')}</AlertDialogCancel>
            <AlertDialogAction
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              onClick={handleIgnoreConfirm}
              data-testid={`worksite-ignore-confirm-${worksite.uid}`}
            >
              {t('actions.ignore', 'Ignore')}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  )
})

interface EmptyCellProps {
  year: number
  isDropTarget?: boolean
  worksiteUid?: string
}

export const EmptyCell = React.memo(function EmptyCell({ year, isDropTarget, worksiteUid }: EmptyCellProps) {
  if (isDropTarget && worksiteUid) {
    return <DroppableYearCell year={year} worksiteUid={worksiteUid} />
  }

  return (
    <div
      className="min-w-28 h-full rounded-sm border border-dashed border-border/30 bg-transparent"
      data-year={year}
    />
  )
})

function DroppableYearCell({ year, worksiteUid }: { year: number; worksiteUid: string }) {
  const { setNodeRef, isOver } = useDroppable({
    id: `drop-${worksiteUid}-${year}`,
    data: { year, worksiteUid },
  })

  return (
    <div
      ref={setNodeRef}
      className={cn(
        'min-w-28 h-16 rounded-sm border border-dashed transition-colors',
        isOver
          ? 'border-primary bg-primary/10 ring-1 ring-primary/30'
          : 'border-border/30 bg-transparent'
      )}
      data-year={year}
      data-testid={`drop-target-${worksiteUid}-${year}`}
    />
  )
}
